import { useTranslation } from '../../../i18n/useTranslation';
import { useAbpPermissions } from '../../../contexts/AbpPermissionsContext';
import { useLicenseColumnToggles } from '../../../hooks/useLicenseColumnToggles';
import type { LicenseFilterState, ManageLicenseListDto } from '../../../types/manageLicenses';
import LicenseListRow from './LicenseListRow';

interface Props {
  rows: ManageLicenseListDto[];
  totalCount: number;
  isLoading: boolean;
  filters: LicenseFilterState;
  onChange: (patch: Partial<LicenseFilterState>) => void;
  selection: Map<string, ManageLicenseListDto>;
  onToggleRow: (row: ManageLicenseListDto) => void;
  onTogglePage: (rows: ManageLicenseListDto[], all: boolean) => void;
  onOpen: (id: string) => void;
  onRequestRowAction: (row: ManageLicenseListDto, action: 'revoke' | 'suspend' | 'reactivate' | 'extend') => void;
}

export default function LicenseListTable({
  rows, totalCount, isLoading, filters, onChange,
  selection, onToggleRow, onTogglePage, onOpen, onRequestRowAction,
}: Props) {
  const { t } = useTranslation();
  const { isHidden } = useLicenseColumnToggles();
  const { hasPermission } = useAbpPermissions();

  const permissions = {
    canRevoke:  hasPermission('learnleaf.App.RevokeLicense'),
    canSuspend: hasPermission('learnleaf.App.SuspendLicense'),
    canManage:  hasPermission('learnleaf.App.ManageLicenses'),
  };

  const allOnPageSelected = rows.length > 0 && rows.every(r => selection.has(r.id));
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));

  const sortable = (field: LicenseFilterState['sort'], label: string) => {
    const active = filters.sort === field;
    const nextDir: 'asc' | 'desc' = active && filters.dir === 'asc' ? 'desc' : 'asc';
    return (
      <button
        type="button"
        onClick={() => onChange({ sort: field, dir: nextDir, page: 1 })}
        className={`text-xs uppercase tracking-wide ${active ? 'text-primary-600' : 'text-neutral-600'}`}
      >
        {label} {active ? (filters.dir === 'asc' ? '▲' : '▼') : ''}
      </button>
    );
  };

  // Plain header cell (non-sortable)
  const th = (label: string) => (
    <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{label}</th>
  );

  return (
    <div>
      {isLoading && (
        <div className="text-sm text-neutral-500 mb-2">{t('App.Admin.Licenses.LicenseListTable.Loading')}</div>
      )}

      <div className="overflow-x-auto border border-neutral-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              {/* Checkbox */}
              <th className="p-2 w-8">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={(e) => onTogglePage(rows, e.target.checked)}
                  aria-label={t('App.Admin.Licenses.LicenseListTable.SelectAllAriaLabel')}
                />
              </th>

              {/* Student email */}
              <th className="p-2 text-left">{sortable('studentEmail', t('App.Admin.Licenses.LicenseListTable.ColStudentEmail'))}</th>

              {/* Optional: studentName */}
              {!isHidden('studentName') && th(t('App.Admin.Licenses.LicenseListTable.ColStudentName'))}

              {/* Book title */}
              <th className="p-2 text-left">{sortable('bookTitle', t('App.Admin.Licenses.LicenseListTable.ColBookTitle'))}</th>

              {/* Optional: publisher */}
              {!isHidden('publisher') && th(t('App.Admin.Licenses.LicenseListTable.ColPublisher'))}

              {/* Type */}
              {th(t('App.Admin.Licenses.LicenseListTable.ColType'))}

              {/* Status */}
              <th className="p-2 text-left">{sortable('status', t('App.Admin.Licenses.LicenseListTable.ColStatus'))}</th>

              {/* Optional: activation */}
              {!isHidden('activation') && (
                <th className="p-2 text-left">{sortable('activationDate', t('App.Admin.Licenses.LicenseListTable.ColActivation'))}</th>
              )}

              {/* Expiration */}
              <th className="p-2 text-left">{sortable('expirationDate', t('App.Admin.Licenses.LicenseListTable.ColExpiration'))}</th>

              {/* Devices */}
              <th className="p-2 text-left">{sortable('activeDeviceCount', t('App.Admin.Licenses.LicenseListTable.ColDevices'))}</th>

              {/* Optional: lastActive */}
              {!isHidden('lastActive') && th(t('App.Admin.Licenses.LicenseListTable.ColLastActive'))}

              {/* Optional: externalLicenseId */}
              {!isHidden('externalLicenseId') && th(t('App.Admin.Licenses.LicenseListTable.ColExternalId'))}

              {/* Optional: bookseller */}
              {!isHidden('bookseller') && th(t('App.Admin.Licenses.LicenseListTable.ColBookseller'))}

              {/* Actions */}
              <th className="p-2 w-12" />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <LicenseListRow
                key={row.id}
                row={row}
                selected={selection.has(row.id)}
                onToggleSelect={() => onToggleRow(row)}
                onOpen={() => onOpen(row.id)}
                hidden={isHidden}
                permissions={permissions}
                onRequestAction={(action) => onRequestRowAction(row, action)}
              />
            ))}
          </tbody>
        </table>

        {rows.length === 0 && !isLoading && (
          <div className="p-6 text-center text-sm text-neutral-500">{t('App.Admin.Licenses.LicenseListTable.NoResults')}</div>
        )}
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="flex items-center gap-3 text-neutral-600">
          <span>
            {t('App.Admin.Licenses.LicenseListTable.PageSummary', { count: totalCount, page: filters.page, totalPages })}
          </span>
          <label className="flex items-center gap-1">
            <span className="text-xs">{t('App.Admin.Licenses.LicenseListTable.RowsLabel')}</span>
            <select
              value={filters.pageSize}
              onChange={(e) => onChange({ pageSize: Number(e.target.value), page: 1 })}
              className="border border-neutral-300 rounded px-1 py-0.5 text-sm"
              aria-label={t('App.Admin.Licenses.LicenseListTable.RowsAriaLabel')}
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={filters.page <= 1}
            onClick={() => onChange({ page: filters.page - 1 })}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Licenses.LicenseListTable.PrevButton')}
          </button>
          <button
            type="button"
            disabled={filters.page >= totalPages}
            onClick={() => onChange({ page: filters.page + 1 })}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Licenses.LicenseListTable.NextButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
